import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ZoneSelector from "@/components/ZoneSelector";
import StripeConnectCard from "@/components/StripeConnectCard";
import UserMenu from "@/components/UserMenu";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Euro,
  Camera,
  Upload,
  X,
  Loader2,
  Save,
  Building2,
  CalendarDays,
  ChevronDown,
  ShieldCheck,
  FileText,
  Lock,
} from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const DAYS = [
  { id: "lundi", label: "Lun" },
  { id: "mardi", label: "Mar" },
  { id: "mercredi", label: "Mer" },
  { id: "jeudi", label: "Jeu" },
  { id: "vendredi", label: "Ven" },
  { id: "samedi", label: "Sam" },
  { id: "dimanche", label: "Dim" },
];

const AVAIL_SLOTS = [
  { id: "matin", label: "Matin (7h-12h)" },
  { id: "apres_midi", label: "Après-midi (12h-18h)" },
  { id: "soir", label: "Soir (18h-21h)" },
  { id: "nuit", label: "Nuit (21h-7h)" },
];

const NurseProfile = () => {
  const { user, token, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [savingPro, setSavingPro] = useState(false);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [savingAvail, setSavingAvail] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const pictureInputRef = useRef(null);

  const [personal, setPersonal] = useState({ name: "", phone: "" });
  const [pro, setPro] = useState({
    zones: [],
    skills: [],
    description: "",
    base_rate: 20,
    night_surcharge: 10,
    weekend_surcharge: 10,
    conventionne: null,
  });
  const [businessInfo, setBusinessInfo] = useState({
    business_name: "",
    siret: "",
    billing_address: "",
    billing_postal_code: "",
    billing_city: "",
    tva_number: "",
  });
  const [billingCityOptions, setBillingCityOptions] = useState([]);
  const [availability, setAvailability] = useState({});
  const [savingCreds, setSavingCreds] = useState(false);
  const [creds, setCreds] = useState({ rpps_number: "", rpps_status: null, rcp_document_file: null, rcp_new: null, is_active: false });

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (user) {
      setPersonal({ name: user.name || "", phone: user.phone || "" });
      setProfilePicture(user.picture || null);
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/nurses/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.onboarding_step < 1) {
          navigate("/nurse/onboarding");
          return;
        }
        setCreds({
          rpps_number: data.rpps_number || "",
          rpps_status: data.rpps_status || null,
          rcp_document_file: data.rcp_document_file || null,
          rcp_new: null,
          is_active: !!data.is_active,
        });
        const normalizedZones = (data.zones || []).map((zone) =>
          typeof zone === "string"
            ? { city: zone, postal_code: "", radius: 10, lat: null, lng: null }
            : {
                city: zone.city || "",
                postal_code: zone.postal_code || "",
                radius: zone.radius || 10,
                lat: zone.lat || null,
                lng: zone.lng || null,
              }
        );
        setPro({
          zones: normalizedZones,
          skills: data.skills || [],
          description: data.description || "",
          base_rate: data.base_rate ?? 20,
          night_surcharge: data.night_surcharge ?? 10,
          weekend_surcharge: data.weekend_surcharge ?? 10,
          conventionne: data.conventionne ?? null,
          business_info: data.business_info || undefined,
        });
        if (data.business_info) {
          setBusinessInfo({
            business_name: data.business_info.business_name || "",
            siret: data.business_info.siret || "",
            billing_address: data.business_info.billing_address || "",
            billing_postal_code: data.business_info.billing_postal_code || "",
            billing_city: data.business_info.billing_city || "",
            tva_number: data.business_info.tva_number || "",
          });
        }
        setAvailability(data.availability || {});
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Load all communes for the billing postal code (a postal code can cover several cities)
  useEffect(() => {
    const fetchBillingCities = async () => {
      if (businessInfo.billing_postal_code?.length === 5) {
        try {
          const res = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${businessInfo.billing_postal_code}&fields=nom`);
          const data = res.ok ? await res.json() : [];
          const cities = (data || []).map((c) => c.nom);
          setBillingCityOptions(cities);
          setBusinessInfo((prev) => {
            if (!prev.billing_city || (cities.length > 0 && !cities.includes(prev.billing_city))) {
              return { ...prev, billing_city: cities[0] || prev.billing_city };
            }
            return prev;
          });
        } catch (e) { console.debug("Billing city lookup failed:", e); }
      } else {
        setBillingCityOptions([]);
      }
    };
    fetchBillingCities();
  }, [businessInfo.billing_postal_code]);

  const toggleAvailability = (day, slot) => {
    const key = `${day}_${slot}`;
    setAvailability((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveBusiness = async (e) => {
    e.preventDefault();
    setSavingBusiness(true);
    try {
      const res = await fetch(`${API}/nurses/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          zones: pro.zones,
          skills: pro.skills,
          description: pro.description,
          base_rate: pro.base_rate,
          night_surcharge: pro.night_surcharge,
          weekend_surcharge: pro.weekend_surcharge,
          business_info: businessInfo,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur lors de l'enregistrement");
      }
      toast.success("Informations de l'entité juridique mises à jour");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleSaveAvailability = async (e) => {
    e.preventDefault();
    setSavingAvail(true);
    try {
      const res = await fetch(`${API}/nurses/availability`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ availability }),
      });
      if (!res.ok) throw new Error("Erreur lors de l'enregistrement des disponibilités");
      toast.success("Disponibilités mises à jour");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingAvail(false);
    }
  };

  const handleRcpFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
    if (!okTypes.includes(file.type)) {
      toast.error("Format accepté : JPG, PNG, WebP ou PDF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCreds((c) => ({ ...c, rcp_new: { name: file.name, type: file.type, data: reader.result } }));
    reader.readAsDataURL(file);
  };

  const openRcpDocument = async () => {
    try {
      const response = await fetch(`${API}/nurses/rcp-document/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Attestation indisponible");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    if (creds.is_active) return;
    if (!creds.rpps_number.trim()) return toast.error("Le numéro RPPS est requis");
    setSavingCreds(true);
    try {
      const payload = { rpps_number: creds.rpps_number.trim() };
      // Only resend the RCP attestation when a new file was selected
      if (creds.rcp_new) payload.rcp_document_file = creds.rcp_new;
      const res = await fetch(`${API}/nurses/credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur lors de l'enregistrement");
      }
      const data = await res.json();
      setCreds((c) => ({
        ...c,
        rpps_status: data.rpps_status || c.rpps_status,
        rcp_document_file: c.rcp_new ? { name: c.rcp_new.name, type: c.rcp_new.type, storage_path: "pending" } : c.rcp_document_file,
        rcp_new: null,
      }));
      toast.success(data.rpps_warning || "Informations mises à jour. Vérification en cours par notre équipe.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingCreds(false);
    }
  };

  const handleSavePersonal = async (e) => {
    e.preventDefault();
    if (!personal.name.trim()) return toast.error("Le nom est requis");
    if (!personal.phone.trim()) return toast.error("Le téléphone est requis");
    setSavingPersonal(true);
    try {
      const res = await fetch(`${API}/auth/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: personal.name.trim(), phone: personal.phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur lors de l'enregistrement");
      await checkAuth();
      toast.success("Informations personnelles mises à jour");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPersonal(false);
    }
  };

  const handlePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("L'image ne doit pas dépasser 3 Mo");
    if (!file.type.startsWith("image/")) return toast.error("Format accepté : JPG, PNG, WebP");
    setUploadingPicture(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result;
        const res = await fetch(`${API}/auth/me/picture`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ picture: base64 }),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Erreur lors de l'envoi de la photo");
        }
        setProfilePicture(base64);
        await checkAuth();
        toast.success("Photo mise à jour !");
      } catch (err) {
        toast.error(err.message);
      } finally {
        setUploadingPicture(false);
        if (pictureInputRef.current) pictureInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePicture = async () => {
    setUploadingPicture(true);
    try {
      const res = await fetch(`${API}/auth/me/picture`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ picture: null }),
      });
      if (!res.ok) throw new Error("Erreur lors de la suppression");
      setProfilePicture(null);
      await checkAuth();
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSavePro = async (e) => {
    e.preventDefault();
    if (pro.zones.length === 0) return toast.error("Veuillez ajouter au moins une zone d'intervention");
    if (pro.conventionne === null || pro.conventionne === undefined) return toast.error("Veuillez indiquer votre conventionnement");
    setSavingPro(true);
    try {
      const res = await fetch(`${API}/nurses/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          zones: pro.zones,
          skills: pro.skills,
          description: pro.description,
          base_rate: pro.base_rate,
          night_surcharge: pro.night_surcharge,
          weekend_surcharge: pro.weekend_surcharge,
          conventionne: pro.conventionne,
          business_info: pro.business_info,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Erreur lors de la mise à jour du profil");
      }
      toast.success("Profil professionnel mis à jour");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPro(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00E073] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1C32]">
      <nav className="bg-[#1F2A38] border-b border-[#2A3A4D]">
        <div className="container-ip">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 w-auto object-contain" />
            </Link>
            <UserMenu userName={user?.name} />
          </div>
        </div>
      </nav>

      <main className="container-ip py-8 max-w-3xl mx-auto">
        <Link
          to="/nurse/dashboard"
          className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Link>

        <h1 className="text-2xl font-bold text-white mb-1">Mon profil</h1>
        <p className="text-[#94A3B8] mb-8">Gérez vos informations sans repasser par l'inscription</p>

        {/* Personal Info */}
        <div className="card-dark p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-[#00E073]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Informations personnelles</h2>
              <p className="text-sm text-[#94A3B8]">Nom, téléphone et photo de profil</p>
            </div>
          </div>

          {/* Photo */}
          <div className="mb-6">
            <Label className="form-label flex items-center gap-2">
              <Camera className="w-4 h-4" /> Photo de profil
            </Label>
            <div className="flex items-center gap-4 mt-2">
              <div className="w-20 h-20 rounded-full bg-[#1F2A38] border-2 border-[#2A3A4D] overflow-hidden flex items-center justify-center flex-shrink-0">
                {profilePicture ? (
                  <img src={profilePicture} alt="Photo" className="w-full h-full object-cover" data-testid="profile-picture-preview" />
                ) : (
                  <Camera className="w-7 h-7 text-[#64748B]" />
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={pictureInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePictureChange}
                  className="hidden"
                  data-testid="profile-picture-input"
                />
                <Button type="button" onClick={() => pictureInputRef.current?.click()} className="btn-secondary" disabled={uploadingPicture} data-testid="upload-picture-btn">
                  {uploadingPicture ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {profilePicture ? "Changer" : "Ajouter"}
                </Button>
                {profilePicture && (
                  <Button type="button" onClick={handleRemovePicture} variant="ghost" className="text-[#94A3B8] hover:text-red-400" disabled={uploadingPicture} data-testid="remove-picture-btn">
                    <X className="w-4 h-4 mr-2" /> Supprimer
                  </Button>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSavePersonal} className="space-y-4">
            <div>
              <Label className="form-label flex items-center gap-2"><User className="w-4 h-4" /> Nom complet</Label>
              <Input
                value={personal.name}
                onChange={(e) => setPersonal({ ...personal, name: e.target.value })}
                className="input-dark"
                placeholder="Marie Dupont"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label className="form-label flex items-center gap-2"><Phone className="w-4 h-4" /> Téléphone</Label>
              <Input
                value={personal.phone}
                onChange={(e) => setPersonal({ ...personal, phone: e.target.value })}
                className="input-dark"
                placeholder="06 12 34 56 78"
                data-testid="profile-phone-input"
              />
            </div>
            <div>
              <Label className="form-label flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
              <Input value={user?.email || ""} className="input-dark bg-[#0D1C32]/50 cursor-not-allowed" disabled readOnly data-testid="profile-email-input" />
              <p className="text-xs text-[#64748B] mt-1">L'email ne peut pas être modifié ici.</p>
            </div>
            <Button type="submit" className="btn-primary" disabled={savingPersonal} data-testid="save-personal-btn">
              {savingPersonal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer mes informations
            </Button>
          </form>
        </div>

        {/* Verification: RPPS & RCP */}
        <div className="card-dark p-6 mb-6" data-testid="verification-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#00E073]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Vérification (RPPS & RCP)</h2>
              <p className="text-sm text-[#94A3B8]">Numéro RPPS et attestation d'assurance responsabilité civile</p>
            </div>
          </div>

          {creds.is_active ? (
            <div className="mb-5 flex items-start gap-2 rounded-lg border border-[#2A3A4D] bg-[#0D1C32] px-4 py-3">
              <Lock className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
              <p className="text-sm text-[#94A3B8]">
                Votre compte a été validé par notre équipe : ces informations ne sont plus modifiables. Pour toute correction, contactez le support.
              </p>
            </div>
          ) : (
            <div className="mb-5 flex items-start gap-2 rounded-lg border border-[#28CAD4]/30 bg-[#28CAD4]/10 px-4 py-3">
              <ShieldCheck className="w-4 h-4 text-[#28CAD4] mt-0.5 shrink-0" />
              <p className="text-sm text-[#CBD5E1]">
                Vous pouvez renseigner ou corriger ces informations tant que votre compte n'a pas été validé par notre équipe.
              </p>
            </div>
          )}

          <form onSubmit={handleSaveCredentials} className="space-y-5">
            <div>
              <Label className="form-label">Numéro RPPS</Label>
              <Input
                value={creds.rpps_number}
                onChange={(e) => setCreds({ ...creds, rpps_number: e.target.value.replace(/\D/g, "") })}
                disabled={creds.is_active}
                placeholder="Ex : 10101010101"
                className={`input-dark ${creds.is_active ? "bg-[#0D1C32]/50 cursor-not-allowed" : ""}`}
                data-testid="rpps-number-input"
              />
              {creds.rpps_status && (
                <p className="text-xs mt-1.5">
                  <span className="text-[#64748B]">Statut RPPS : </span>
                  <span className={creds.rpps_status === "valid" ? "text-[#00E073]" : "text-yellow-500"}>
                    {creds.rpps_status === "valid" ? "Confirmé (ANS)" : "À vérifier manuellement"}
                  </span>
                </p>
              )}
            </div>

            <div>
              <Label className="form-label">Attestation d'assurance RC professionnelle (RCP)</Label>
              <div className="flex items-center gap-3 flex-wrap">
                {creds.rcp_new ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#00E073]" data-testid="rcp-current-status">
                    <FileText className="w-4 h-4" /> {creds.rcp_new.name} (prête à être envoyée)
                  </span>
                ) : creds.rcp_document_file ? (
                  <button type="button" onClick={openRcpDocument}
                    className="inline-flex items-center gap-1.5 text-sm text-[#00E073] hover:underline" data-testid="rcp-current-link">
                    <FileText className="w-4 h-4" /> Voir l'attestation actuelle
                  </button>
                ) : (
                  <span className="text-sm text-[#64748B]">Aucune attestation fournie</span>
                )}
                {!creds.is_active && (
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#2A3A4D] bg-[#0D1C32] text-sm text-[#CBD5E1] cursor-pointer hover:border-[#00E073]/40">
                    <Upload className="w-4 h-4" />
                    {creds.rcp_document_file || creds.rcp_new ? "Remplacer" : "Téléverser"}
                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handleRcpFile} data-testid="rcp-file-input" />
                  </label>
                )}
              </div>
              <p className="text-xs text-[#64748B] mt-1.5">JPG, PNG, WebP ou PDF — 5 Mo max.</p>
            </div>

            {!creds.is_active && (
              <div className="flex justify-end">
                <Button type="submit" disabled={savingCreds} className="btn-primary" data-testid="save-credentials-btn">
                  {savingCreds ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* Professional Info */}
        <div className="card-dark p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#00E073]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Profil professionnel</h2>
              <p className="text-sm text-[#94A3B8]">Zones, tarifs et présentation</p>
            </div>
          </div>

          <form onSubmit={handleSavePro} className="space-y-6">
            <div>
              <Label className="form-label flex items-center gap-2"><MapPin className="w-4 h-4" /> Zones d'intervention</Label>
              <ZoneSelector zones={pro.zones} onChange={(newZones) => setPro({ ...pro, zones: newZones })} />
            </div>

            <div>
              <Label className="form-label">Présentation (optionnel)</Label>
              <Textarea
                value={pro.description}
                onChange={(e) => setPro({ ...pro, description: e.target.value })}
                className="input-dark min-h-[80px]"
                placeholder="Quelques mots sur votre expérience et votre approche..."
                data-testid="profile-description-input"
              />
            </div>

            <div>
              <Label className="form-label flex items-center gap-2"><Euro className="w-4 h-4" /> Tarifs indicatifs</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-[#64748B]">Base (€)</Label>
                  <Input type="number" value={pro.base_rate} onChange={(e) => setPro({ ...pro, base_rate: parseFloat(e.target.value) })} className="input-dark" min="0" step="0.5" data-testid="profile-base-rate-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#64748B]">Nuit (+€)</Label>
                  <Input type="number" value={pro.night_surcharge} onChange={(e) => setPro({ ...pro, night_surcharge: parseFloat(e.target.value) })} className="input-dark" min="0" step="0.5" data-testid="profile-night-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#64748B]">W-E (+€)</Label>
                  <Input type="number" value={pro.weekend_surcharge} onChange={(e) => setPro({ ...pro, weekend_surcharge: parseFloat(e.target.value) })} className="input-dark" min="0" step="0.5" data-testid="profile-weekend-input" />
                </div>
              </div>
              <p className="text-xs text-[#64748B] mt-2">Grille indicative – vous restez libre de fixer vos honoraires.</p>
            </div>

            <div>
              <Label className="form-label">Conventionnement *</Label>
              <p className="text-xs text-[#64748B] mb-3">
                Les infirmiers <strong>conventionnés</strong> ne sont visibles que par les patients étrangers (non affiliés à l'Assurance Maladie française). Les <strong>non conventionnés</strong> sont visibles par tous les patients.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPro({ ...pro, conventionne: false })}
                  className={`text-left p-3 rounded-lg border transition-colors ${pro.conventionne === false ? "border-[#00E073] bg-[#00E073]/10" : "border-[#2A3A4D] bg-[#0D1C32] hover:border-[#00E073]/40"}`}
                  data-testid="conventionne-non-btn"
                >
                  <p className="text-white font-medium text-sm">Infirmier libéral non conventionné</p>
                  <p className="text-xs text-[#64748B] mt-1">Visible par tous les patients</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPro({ ...pro, conventionne: true })}
                  className={`text-left p-3 rounded-lg border transition-colors ${pro.conventionne === true ? "border-[#00E073] bg-[#00E073]/10" : "border-[#2A3A4D] bg-[#0D1C32] hover:border-[#00E073]/40"}`}
                  data-testid="conventionne-oui-btn"
                >
                  <p className="text-white font-medium text-sm">Infirmier libéral conventionné</p>
                  <p className="text-xs text-[#64748B] mt-1">Visible uniquement par les patients étrangers</p>
                </button>
              </div>
            </div>

            <Button type="submit" className="btn-primary" disabled={savingPro || pro.zones.length === 0} data-testid="save-pro-btn">
              {savingPro ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer mon profil pro
            </Button>
          </form>
        </div>

        {/* Business / Legal entity Info */}
        <div className="card-dark p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#28CAD4]/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#28CAD4]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Entité juridique</h2>
              <p className="text-sm text-[#94A3B8]">Informations pour la facturation aux patients</p>
            </div>
          </div>

          <form onSubmit={handleSaveBusiness} className="space-y-4">
            <div>
              <Label className="form-label">Raison sociale / Nom</Label>
              <Input
                value={businessInfo.business_name}
                onChange={(e) => setBusinessInfo({ ...businessInfo, business_name: e.target.value })}
                className="input-dark"
                placeholder="Ex: Cabinet Infirmier Dupont ou Marie Dupont"
                data-testid="business-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Numéro SIRET</Label>
                <Input
                  value={businessInfo.siret}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, siret: e.target.value })}
                  className="input-dark"
                  placeholder="14 chiffres"
                  maxLength={14}
                  data-testid="siret-input"
                />
              </div>
              <div>
                <Label className="form-label">N° TVA (optionnel)</Label>
                <Input
                  value={businessInfo.tva_number}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, tva_number: e.target.value })}
                  className="input-dark"
                  placeholder="FR + 11 chiffres"
                  data-testid="tva-input"
                />
              </div>
            </div>
            <div>
              <Label className="form-label">Adresse de facturation</Label>
              <Input
                value={businessInfo.billing_address}
                onChange={(e) => setBusinessInfo({ ...businessInfo, billing_address: e.target.value })}
                className="input-dark"
                placeholder="123 rue de la Santé"
                data-testid="billing-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Code postal</Label>
                <Input
                  value={businessInfo.billing_postal_code}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, billing_postal_code: e.target.value })}
                  className="input-dark"
                  placeholder="75015"
                  maxLength={5}
                  data-testid="billing-postal-input"
                />
              </div>
              <div>
                <Label className="form-label">Ville</Label>
                <div className="relative">
                  <select
                    value={businessInfo.billing_city}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, billing_city: e.target.value })}
                    className="input-dark w-full appearance-none pr-8"
                    data-testid="billing-city-select"
                  >
                    {!businessInfo.billing_city && <option value="">Renseignez le code postal</option>}
                    {Array.from(new Set([businessInfo.billing_city, ...billingCityOptions].filter(Boolean))).map((c) => (
                      <option key={c} value={c} className="bg-[#1F2A38] text-white">{c}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
                </div>
              </div>
            </div>
            <Button type="submit" className="btn-primary" disabled={savingBusiness} data-testid="save-business-btn">
              {savingBusiness ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer l'entité juridique
            </Button>
          </form>
        </div>

        {/* Availability */}
        <div className="card-dark p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#00E073]/10 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-[#00E073]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Disponibilités</h2>
              <p className="text-sm text-[#94A3B8]">Vos créneaux habituels par demi-journée</p>
            </div>
          </div>

          <form onSubmit={handleSaveAvailability} className="space-y-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left text-xs text-[#64748B] pb-3"></th>
                    {DAYS.map((day) => (
                      <th key={day.id} className="text-center text-xs text-[#94A3B8] pb-3 px-1">{day.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {AVAIL_SLOTS.map((slot) => (
                    <tr key={slot.id}>
                      <td className="text-xs text-[#94A3B8] pr-3 py-1 whitespace-nowrap">{slot.label}</td>
                      {DAYS.map((day) => {
                        const key = `${day.id}_${slot.id}`;
                        const isAvailable = availability[key];
                        return (
                          <td key={day.id} className="px-1 py-1">
                            <button
                              type="button"
                              onClick={() => toggleAvailability(day.id, slot.id)}
                              className={`w-full h-10 rounded-lg text-xs transition-colors ${
                                isAvailable
                                  ? "bg-[#00E073]/30 text-[#00E073] border border-[#00E073]/50"
                                  : "bg-[#2A3A4D] text-[#64748B] hover:bg-[#00E073]/10"
                              }`}
                              data-testid={`avail-${key}`}
                            >
                              {isAvailable ? "✓" : ""}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#64748B]">Cliquez sur les créneaux où vous êtes généralement disponible. Vous pourrez ajuster ponctuellement via votre calendrier.</p>
            <Button type="submit" className="btn-primary" disabled={savingAvail} data-testid="save-availability-btn">
              {savingAvail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer mes disponibilités
            </Button>
          </form>
        </div>

        {/* Payments / Stripe */}
        <div className="card-dark p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#28CAD4]/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#28CAD4]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Paiements</h2>
              <p className="text-sm text-[#94A3B8]">Compte bancaire via Stripe pour recevoir vos honoraires</p>
            </div>
          </div>
          <StripeConnectCard />
        </div>
      </main>
    </div>
  );
};

export default NurseProfile;
